import React, { useEffect, useMemo, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

function isImageFile(file) {
  const mime = String(file && file.mimeType ? file.mimeType : '').toLowerCase();
  const url = String(file && file.url ? file.url : '').toLowerCase();
  return mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg)$/.test(url);
}

function isVideoFile(file) {
  const mime = String(file && file.mimeType ? file.mimeType : '').toLowerCase();
  const url = String(file && file.url ? file.url : '').toLowerCase();
  return mime.startsWith('video/') || /\.(mp4|webm|mov)$/.test(url);
}

function RichTextEditor({
  value,
  onChange,
  placeholder = 'Write content...',
  mediaItems = [],
  onRefreshMedia,
}) {
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');

  const imageItems = useMemo(() => mediaItems.filter(isImageFile), [mediaItems]);
  const videoItems = useMemo(() => mediaItems.filter(isVideoFile), [mediaItems]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor: instance }) => {
      onChange(instance.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;

    const current = editor.getHTML();
    const next = value || '';
    if (current !== next) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (!imageItems.some((item) => item.url === selectedImageUrl)) {
      setSelectedImageUrl(imageItems[0] ? imageItems[0].url : '');
    }
  }, [imageItems, selectedImageUrl]);

  useEffect(() => {
    if (!videoItems.some((item) => item.url === selectedVideoUrl)) {
      setSelectedVideoUrl(videoItems[0] ? videoItems[0].url : '');
    }
  }, [videoItems, selectedVideoUrl]);

  const setLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('URL', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertImage = () => {
    if (!editor) return;
    const src = selectedImageUrl || window.prompt('Image URL (e.g. /media/2026/03/photo.jpg)');
    if (!src) return;

    const caption = window.prompt('Optional caption', '') || '';

    editor.chain().focus().setImage({ src, alt: caption }).run();

    if (caption) {
      editor.chain().focus().insertContent(`<p><em>${caption.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</em></p>`).run();
    }
  };

  const insertVideo = () => {
    if (!editor) return;
    const src = selectedVideoUrl || window.prompt('Video URL (e.g. /media/2026/03/clip.mp4)');
    if (!src) return;

    editor
      .chain()
      .focus()
      .insertContent(`<figure><video controls src="${src}"></video></figure>`)
      .run();
  };

  if (!editor) {
    return <div className="rich-editor-shell">Loading editor...</div>;
  }

  return (
    <div className="rich-editor-shell">
      <div className="rich-editor-toolbar">
        <button type="button" className="secondary-button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          H2
        </button>
        <button type="button" className="secondary-button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          H3
        </button>
        <button type="button" className="secondary-button" onClick={() => editor.chain().focus().toggleBold().run()}>
          Bold
        </button>
        <button type="button" className="secondary-button" onClick={() => editor.chain().focus().toggleItalic().run()}>
          Italic
        </button>
        <button type="button" className="secondary-button" onClick={() => editor.chain().focus().toggleBulletList().run()}>
          Bullet List
        </button>
        <button type="button" className="secondary-button" onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          Numbered List
        </button>
        <button type="button" className="secondary-button" onClick={setLink}>
          Link
        </button>
        <button type="button" className="secondary-button" onClick={insertImage}>
          Image
        </button>
        <button type="button" className="secondary-button" onClick={insertVideo}>
          Video
        </button>
      </div>
      <div className="media-picker-row">
        <label>
          Image source
          <select
            value={selectedImageUrl}
            onChange={(event) => setSelectedImageUrl(event.target.value)}
          >
            <option value="">Manual URL prompt</option>
            {imageItems.map((item) => (
              <option key={item.url} value={item.url}>
                {item.path || item.url}
              </option>
            ))}
          </select>
        </label>
        <label>
          Video source
          <select
            value={selectedVideoUrl}
            onChange={(event) => setSelectedVideoUrl(event.target.value)}
          >
            <option value="">Manual URL prompt</option>
            {videoItems.map((item) => (
              <option key={item.url} value={item.url}>
                {item.path || item.url}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="secondary-button"
          onClick={() => (onRefreshMedia ? onRefreshMedia() : null)}
        >
          Refresh Media
        </button>
      </div>
      <EditorContent editor={editor} className="rich-editor-content" />
    </div>
  );
}

export default RichTextEditor;
